/**
 * @file useAuth.js
 * @location src/hooks/useAuth.js
 * @role Supabase Auth 세션 구독 + 인증 메서드 제공.
 *
 * 이메일/패스워드 표준 로그인: signInWithPassword · signUpWithPassword · sendPasswordReset.
 *   (매직링크(signInWithOtp)는 표준 비번 통일로 폐기 — 매일 쓰는 도구엔 매번 메일 클릭이 무겁고,
 *    비번 없는 유저를 만들어 개인정보 변경 재인증을 막았음.)
 * Google OAuth(signInWithGoogle): 한국 B2B 사실상 표준 Google Workspace 계정의 마찰 제거.
 *   카톡·네이버 소셜은 razor ② 충돌(채팅 의존 ↑)·B2B 개인정체성 분리 원칙으로 의도적 제외.
 * 패스워드 리셋 확정: PASSWORD_RECOVERY 이벤트 → passwordRecovery=true → updatePassword.
 * 반환: { session, user, loading, passwordRecovery, signInWithPassword, signUpWithPassword, signInWithGoogle, sendPasswordReset, updatePassword, signOut }.
 */
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../integrations/supabase/client.js';

export function useAuth() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [passwordRecovery, setPasswordRecovery] = useState(false);

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session ?? null);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s ?? null);
      if (event === 'PASSWORD_RECOVERY') setPasswordRecovery(true);
      // signOut 시 recovery trap 회피
      else if (event === 'SIGNED_OUT') setPasswordRecovery(false);
    });
    return () => { active = false; sub.subscription.unsubscribe(); };
  }, []);

  const signInWithPassword = useCallback(async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }, []);

  const signUpWithPassword = useCallback(async (email, password) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: window.location.origin },
    });
    if (error) throw error;
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
    if (error) throw error;
  }, []);

  const sendPasswordReset = useCallback(async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
    if (error) throw error;
  }, []);

  const updatePassword = useCallback(async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    // recovery 종료는 caller 책임 — NewPasswordScreen 이 「완료」 메시지 표시 후 exitPasswordRecovery 호출
  }, []);

  /** 패스워드 리셋 확정 후 「완료」 표시까지 NewPasswordScreen 을 살려두기 위한 명시 종료. */
  const exitPasswordRecovery = useCallback(() => {
    setPasswordRecovery(false);
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }, []);

  return {
    session,
    user: session?.user ?? null,
    loading,
    passwordRecovery,
    signInWithPassword,
    signUpWithPassword,
    signInWithGoogle,
    sendPasswordReset,
    updatePassword,
    exitPasswordRecovery,
    signOut,
  };
}
